import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import Typography from '@mui/material/Typography'
import Slider from '@mui/material/Slider'
import {
  Filters,
  CheckBoxFieldv2,
  RadioButtonsV2,
  RadioButtonType,
} from '@labrav/react-components'
import {
  AMOUNT_MAX,
  AMOUNT_MIN,
  AMOUNT_STEP,
  CREATED_OPTIONS,
  EXPIRY_OPTIONS,
  FilterOption,
  FundingFilterState,
  PROGRAM_OPTIONS,
  STATUS_OPTIONS,
  UPDATED_OPTIONS,
  emptyFilterState,
} from './fundingFilters'
import { useStyles } from './FundingRequestsFilters.styles'

interface FundingRequestsFiltersProps {
  /** The committed filter state — seeds the draft and resets it on external change. */
  applied: FundingFilterState
  onApply: (state: FundingFilterState) => void
  onClear: () => void
}

const toRadioMappings = (options: FilterOption[]) =>
  options.map(option => ({
    key: option.key,
    label: option.label,
    type: RadioButtonType.STANDARD,
  }))

const labelFor = (options: FilterOption[], key: string) =>
  options.find(option => option.key === key)?.label

const formatMoney = (value: number) => `$${value.toLocaleString('en-US')}`

const toggleInList = (list: string[], key: string) =>
  list.includes(key) ? list.filter(item => item !== key) : [...list, key]

export const FundingRequestsFilters = ({
  applied,
  onApply,
  onClear,
}: FundingRequestsFiltersProps) => {
  const { classes } = useStyles()
  const [draft, setDraft] = useState<FundingFilterState>(applied)

  // Keep the draft in sync when the committed filters change from the outside
  // (a chip deleted below the bar, or a Clear elsewhere).
  useEffect(() => {
    setDraft(applied)
  }, [applied])

  const checkboxGroup = (
    options: FilterOption[],
    selected: string[],
    onToggle: (key: string) => void
  ) => (
    <div className={classes.filterGroup}>
      {options.map(option => (
        <CheckBoxFieldv2
          key={option.key}
          keyName={`filter-${option.key}`}
          title={option.label}
          value={selected.includes(option.key)}
          onChangeBox={() => onToggle(option.key)}
        />
      ))}
    </div>
  )

  const radioGroup = (
    options: FilterOption[],
    selected: string,
    onSelect: (key: string) => void
  ) => (
    <RadioButtonsV2
      vertical
      radioButtonMappings={toRadioMappings(options)}
      checkedValue={{ key: selected, value: selected }}
      handleChange={(event: ChangeEvent<HTMLInputElement>) =>
        onSelect(event.target.value)
      }
    />
  )

  const sections = useMemo(
    () => [
      {
        title: 'Created at',
        type: 'radio' as const,
        selectedValues:
          draft.createdWithin !== 'all'
            ? labelFor(CREATED_OPTIONS, draft.createdWithin)
            : undefined,
        content: radioGroup(CREATED_OPTIONS, draft.createdWithin, key =>
          setDraft(prev => ({ ...prev, createdWithin: key }))
        ),
      },
      {
        title: 'Last updated',
        type: 'radio' as const,
        selectedValues:
          draft.updatedWithin !== 'all'
            ? labelFor(UPDATED_OPTIONS, draft.updatedWithin)
            : undefined,
        content: radioGroup(UPDATED_OPTIONS, draft.updatedWithin, key =>
          setDraft(prev => ({ ...prev, updatedWithin: key }))
        ),
      },
      {
        title: 'Expiry date',
        type: 'radio' as const,
        selectedValues:
          draft.expiryWithin !== 'all'
            ? labelFor(EXPIRY_OPTIONS, draft.expiryWithin)
            : undefined,
        content: radioGroup(EXPIRY_OPTIONS, draft.expiryWithin, key =>
          setDraft(prev => ({ ...prev, expiryWithin: key }))
        ),
      },
      {
        title: 'Funding programs',
        type: 'checkbox' as const,
        selectedValues: [],
        content: checkboxGroup(PROGRAM_OPTIONS, draft.programNames, key =>
          setDraft(prev => ({
            ...prev,
            programNames: toggleInList(prev.programNames, key),
          }))
        ),
      },
      {
        title: 'Status',
        type: 'checkbox' as const,
        selectedValues: [],
        content: checkboxGroup(STATUS_OPTIONS, draft.statuses, key =>
          setDraft(prev => ({
            ...prev,
            statuses: toggleInList(prev.statuses, key),
          }))
        ),
      },
      {
        title: 'Amount',
        type: 'custom' as const,
        content: (
          <div className={classes.amountSlider}>
            <Slider
              value={draft.amount}
              min={AMOUNT_MIN}
              max={AMOUNT_MAX}
              step={AMOUNT_STEP}
              valueLabelDisplay="auto"
              valueLabelFormat={value => formatMoney(value as number)}
              onChange={(_, value) =>
                setDraft(prev => ({
                  ...prev,
                  amount: value as [number, number],
                }))
              }
            />
            <div className={classes.amountBounds}>
              <Typography component="span" className={classes.bound}>
                {formatMoney(draft.amount[0])}
              </Typography>
              <Typography component="span" className={classes.bound}>
                {formatMoney(draft.amount[1])}
              </Typography>
            </div>
          </div>
        ),
      },
    ],
    // `draft` is the only value the section content actually varies on. The
    // referenced helpers (radioGroup/checkboxGroup) close over `setDraft` (the
    // stable useState setter) and the OPTION constants are module-level, so
    // none of them need to be dependencies — listing the in-component helpers
    // would recreate `sections` every render and defeat the memo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft]
  )

  return (
    <Filters
      sections={sections}
      applyFilters={() => onApply(draft)}
      clearFilters={() => {
        setDraft(emptyFilterState())
        onClear()
      }}
      minWidth="320px"
    />
  )
}

export default FundingRequestsFilters
